import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson } from "../../api/rest";
import {
	describeCron,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtRelative,
	Panel,
	Spinner,
	StatusChip,
} from "../../components/flows/ui";
import type { ScheduleRow } from "../../types/flows";

// Ordered by next fire, which is the question this page answers: what happens
// next, and is anything about to fire that should not.

const FlowsSchedule = () => {
	const [rows, setRows] = useState<ScheduleRow[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		fetchJson<ScheduleRow[]>("/flows/schedule")
			.then(setRows)
			.catch((e) => setError(String(e)));
	}, []);

	if (error) return <ErrorBanner error={error} />;
	if (!rows) return <Spinner />;

	return (
		<Panel title="Upcoming fires" hint="every schedule across every flow">
			{rows.length === 0 ? (
				<Empty>
					No flow has a schedule. Add one on a flow's Schedule tab — a schedule
					only fires a published flow.
				</Empty>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full text-left text-[13px]">
						<thead>
							<tr className="border-b border-slate-200 text-slate-500">
								<th className="pb-2 pr-3 font-medium">Next fire</th>
								<th className="pb-2 pr-3 font-medium">Flow</th>
								<th className="pb-2 pr-3 font-medium">Schedule</th>
								<th className="pb-2 pr-3 font-medium">Timezone</th>
								<th className="pb-2 pr-3 font-medium">Enabled</th>
								<th className="pb-2 font-medium">Last fired</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr
									key={row.id}
									className="border-b border-slate-100 last:border-0"
								>
									<td
										className="py-2.5 pr-3 text-slate-700"
										title={fmtDateTime(row.nextRunAt)}
									>
										{row.nextRunAt ? fmtRelative(row.nextRunAt) : "—"}
									</td>
									<td className="py-2.5 pr-3">
										<Link
											to={`/flows/${row.flowId}`}
											className="font-medium text-slate-700 hover:text-primary"
										>
											{row.flowName}
										</Link>
										<span className="ml-1.5">
											<StatusChip status={row.flowStatus} />
										</span>
									</td>
									<td className="py-2.5 pr-3">
										<p className="text-slate-700">
											{describeCron(row.cronExpression)}
										</p>
										<p className="font-mono text-[11px] text-slate-400">
											{row.cronExpression}
											{row.name && ` · ${row.name}`}
										</p>
									</td>
									<td className="py-2.5 pr-3 text-slate-500">{row.timezone}</td>
									<td className="py-2.5 pr-3">
										{row.enabled ? (
											<span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
												on
											</span>
										) : (
											<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
												off
											</span>
										)}
									</td>
									<td className="py-2.5 text-slate-500">
										{row.lastFiredAt ? fmtRelative(row.lastFiredAt) : "never"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</Panel>
	);
};

export default FlowsSchedule;
